// src/disc/DiscComparison.js
import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    CircularProgress,
    Alert,
    AlertTitle,
} from '@mui/material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import DiscComparisonTypeSelector from './DiscComparisonTypeSelector';
import DiscComparisonSetup from './DiscComparisonSetup';
import DiscComparisonResults from './DiscComparisonResults';

export default function DiscComparison() {
    const { authToken, showAlert, project, organization, language } = useStore.getState();
    const { t } = useTranslation();

    // Estados principales
    const [currentStep, setCurrentStep] = useState('typeSelection');
    const [analysisType, setAnalysisType] = useState('');

    // Estados de datos
    const [positions, setPositions] = useState([]);
    const [targetPosition, setTargetPosition] = useState(null);
    const [discTests, setDiscTests] = useState([]);
    const [selectedTests, setSelectedTests] = useState({});
    const [bossTestId, setBossTestId] = useState(null);
    const [selectedCompetencies, setSelectedCompetencies] = useState([]); // ✅ NUEVO

    // Estado de contexto de equipo
    const [teamContext, setTeamContext] = useState({
        name: '',
        description: '',
        objectives: '',
    });

    // Estados de carga y análisis
    const [loading, setLoading] = useState({
        positions: false,
        tests: false,
        analysis: false,
    });
    const [currentAnalysis, setCurrentAnalysis] = useState(null);
    const [analysisError, setAnalysisError] = useState(null);

    // Cargar datos iniciales
    useEffect(() => {
        if (project && authToken) {
            loadPositions();
        }
    }, [project, authToken]);

    // useEffect que carga tests DESPUÉS de seleccionar posición
    useEffect(() => {
        if (!project || !authToken || currentStep !== 'setup') return;

        if (analysisType === 'teamAnalysis') {
            loadDiscTests(null);
        }
        else if ((analysisType === 'compareCandidates' || analysisType === 'candidateVsBoss') && targetPosition) {
            loadDiscTests(targetPosition.uid);
        }
    }, [currentStep, analysisType, targetPosition, project, authToken]);

    // Función para cargar cargos
    const loadPositions = async () => {
        if (!project || !authToken) return;

        try {
            setLoading(prev => ({ ...prev, positions: true }));
            const { data } = await api.get('/positions', {
                params: { projectId: project },
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (data.response && data.response.status_response === "OK") {
                setPositions(data.response.data || []);
            } else {
                setPositions([]);
            }
        } catch (error) {
            console.error('Error al cargar cargos:', error);
            showAlert(t('positions.loadError'), 'error');
            setPositions([]);
        } finally {
            setLoading(prev => ({ ...prev, positions: false }));
        }
    };

    // Función para cargar tests DISC completados
    const loadDiscTests = async (positionId = null) => {
        if (!project || !authToken) return;

        try {
            setLoading(prev => ({ ...prev, tests: true }));

            setDiscTests([]);
            setSelectedTests({});
            setBossTestId(null);

            const params = { projectId: project };

            if (positionId) {
                params.positionId = positionId;
            }

            const { data } = await api.get('/disc-tests/latest-by-project', {
                params,
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (data.response && data.response.status_response === "OK") {
                const validTests = (data.response.data || []).filter(test =>
                    test.discResults?.finalScores &&
                    Object.keys(test.discResults.finalScores).length > 0
                );
                setDiscTests(validTests);
            } else {
                setDiscTests([]);
            }
        } catch (error) {
            console.error('Error al cargar tests DISC:', error);
            showAlert(t('disc.discComparisons.testsLoadError'), 'error');
            setDiscTests([]);
        } finally {
            setLoading(prev => ({ ...prev, tests: false }));
        }
    };

    // Handler para cambio de posición
    const handleTargetPositionChange = (newPosition) => {
        if (targetPosition?.uid !== newPosition?.uid) {
            setSelectedTests({});
            setBossTestId(null);
        }

        setTargetPosition(newPosition);
    };

    // ✅ NUEVO: Handler para cambio de competencias
    const handleCompetenciesChange = (competencies) => {
        setSelectedCompetencies(competencies);
    };

    // Función para seleccionar tipo de análisis
    const handleAnalysisTypeSelection = (type) => {
        setAnalysisType(type);
        setCurrentStep('setup');

        setDiscTests([]);
        setSelectedTests({});
        setBossTestId(null);
        setTargetPosition(null);
        setSelectedCompetencies([]); // ✅ NUEVO: Limpiar competencias

        if (type !== 'teamAnalysis') {
            setTeamContext({ name: '', description: '', objectives: '' });
        }
        if (type !== 'candidateVsBoss') {
            setBossTestId(null);
        }
    };

    // Función para manejar selección de tests
    const handleTestSelection = (testId) => {
        setSelectedTests(prev => {
            const newSelection = { ...prev };
            if (newSelection[testId]) {
                delete newSelection[testId];
                if (bossTestId === testId) {
                    setBossTestId(null);
                }
            } else {
                newSelection[testId] = true;
            }
            return newSelection;
        });
    };

    // Función para marcar test como jefe
    const handleBossSelection = (testId) => {
        if (!selectedTests[testId]) {
            showAlert(t('disc.discComparisons.selectTestFirst'), 'warning');
            return;
        }
        setBossTestId(testId === bossTestId ? null : testId);
    };

    // Función para validar si se puede iniciar el análisis
    const canStartAnalysis = () => {
        const hasAnalysisType = analysisType !== '';
        const hasMinTests = Object.keys(selectedTests).length >= 2;

        if (analysisType === 'compareCandidates' || analysisType === 'candidateVsBoss') {
            const hasPosition = targetPosition !== null;
            const hasBoss = analysisType === 'candidateVsBoss' ? bossTestId !== null : true;
            return hasAnalysisType && hasPosition && hasMinTests && hasBoss;
        } else if (analysisType === 'teamAnalysis') {
            const hasTeamContext =
                teamContext.name.trim() !== '' &&
                teamContext.description.trim() !== '' &&
                teamContext.objectives.trim() !== '';
            return hasAnalysisType && hasMinTests && hasTeamContext;
        }

        return false;
    };

    // Función para iniciar el análisis
    const handleStartAnalysis = async () => {
        if (!canStartAnalysis()) {
            showAlert(t('completeRequiredFields'), 'warning');
            return;
        }

        try {
            setLoading(prev => ({ ...prev, analysis: true }));
            setAnalysisError(null);

            // Preparar competencias para enviar
            const competenciesFormatted = selectedCompetencies.map(comp => ({
                id: comp.id || comp.uid,
                name: comp.name || comp.capabilityName,
                description: comp.description || ''
            }));

            const analysisData = {
                analysis_type: analysisType,
                position_id: targetPosition?.uid || null,
                position_name: targetPosition?.name || null,
                position_description: targetPosition?.descripcion || null,
                selected_tests: selectedTests,
                boss_test_id: bossTestId,
                team_context: analysisType === 'teamAnalysis' ? teamContext : null,
                competencies: competenciesFormatted.length > 0 ? competenciesFormatted : undefined,
                project: project,
                organization: organization,
                language: language || 'es',
            };

            const { data } = await api.post(
                '/disc-comparisons',
                analysisData,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            );

            if (data.response && data.response.status_response === "OK") {
                showAlert(t('disc.discComparisons.analysisStarted'), 'success');

                // ✅ CAMBIO: Solo guardar el ID de la comparación
                setCurrentAnalysis({
                    id: data.response.data.id,
                    analysisType: analysisType // Para saber qué vista renderizar
                });

                setCurrentStep('results');
                setLoading(prev => ({ ...prev, analysis: false })); // ✅ Quitar loading aquí
            } else {
                throw new Error(data.response?.message || 'Error desconocido');
            }
        } catch (error) {
            console.error('Error al iniciar análisis:', error);
            setAnalysisError(error.response?.data?.message || error.message);
            showAlert(t('disc.discComparisons.analysisCreateError'), 'error');
            setLoading(prev => ({ ...prev, analysis: false }));
        }
    };

    // Función para volver a la selección de tipo
    const handleBackToTypeSelection = () => {
        setCurrentStep('typeSelection');
        setAnalysisType('');
        setTargetPosition(null);
        setSelectedTests({});
        setBossTestId(null);
        setSelectedCompetencies([]); // ✅ NUEVO: Limpiar competencias
        setTeamContext({ name: '', description: '', objectives: '' });
        setAnalysisError(null);
    };

    // Función para formatear fecha
    const formatDate = (timestamp) => {
        if (!timestamp?._seconds) return t('dateNotAvailable');
        const date = new Date(timestamp._seconds * 1000);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    // Obtener el paso actual para el stepper
    const getActiveStep = () => {
        if (currentStep === 'typeSelection') return 0;
        if (currentStep === 'setup') return 1;
        if (currentStep === 'results') return 2;
        return 0;
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 4 }}>
                <Stepper activeStep={getActiveStep()} alternativeLabel>
                    <Step>
                        <StepLabel>{t('disc.discComparisons.analysisType')}</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>{t('disc.discComparisons.configuration')}</StepLabel>
                    </Step>
                    <Step>
                        <StepLabel sx={{ textTransform: 'capitalize' }}>
                            {t('disc.discComparisons.results')}
                        </StepLabel>
                    </Step>
                </Stepper>
            </Box>

            <>
                {currentStep === 'typeSelection' && (
                    <DiscComparisonTypeSelector
                        selectedType={analysisType}
                        onTypeSelect={handleAnalysisTypeSelection}
                    />
                )}

                {currentStep === 'setup' && (
                    <DiscComparisonSetup
                        analysisType={analysisType}
                        positions={positions}
                        targetPosition={targetPosition}
                        discTests={discTests}
                        selectedTests={selectedTests}
                        bossTestId={bossTestId}
                        teamContext={teamContext}
                        loading={loading}
                        onTargetPositionChange={handleTargetPositionChange}
                        onTestSelection={handleTestSelection}
                        onBossSelection={handleBossSelection}
                        onTeamContextChange={setTeamContext}
                        onCompetenciesChange={handleCompetenciesChange}
                        onStartAnalysis={handleStartAnalysis}
                        onBackToTypeSelection={handleBackToTypeSelection}
                        canStartAnalysis={canStartAnalysis}
                        formatDate={formatDate}
                    />
                )}

                {currentStep === 'results' && currentAnalysis && (
                    <DiscComparisonResults
                        comparisonId={currentAnalysis.id}
                        analysisType={currentAnalysis.analysisType}
                        onBack={handleBackToTypeSelection}
                        onRetry={() => {
                            // Volver al setup sin limpiar datos
                            setCurrentStep('setup');
                            setCurrentAnalysis(null);
                        }}
                    />
                )}
            </>
        </Box>
    );
}